import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { AxisDomain } from 'recharts';

import {
  IDashboardChartData,
  IDashboardHomeCardMetricKey,
  IDashboardHomeCardMetrics,
  IDashboardLiveData,
} from '~/src/dash/api/dashboards/home';
import { getSmartFloorLiveData } from '~/src/dash/apiV2/smartFloors/realtime';
import { DashboardCard } from '~/src/dash/components/cards/DashboardCardsV2';
import {
  CHART_COLORS,
  IDashboardChartMetric,
  IScale,
  mergeCompareData,
} from '~/src/dash/components/charts/common';
import { CompareChart } from '~/src/dash/components/charts/CompareChart';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  Row,
  Rows,
  spacing,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useInterval } from '~/src/dash/utils/hooks';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useSmartFloorsLiveStore } from '~/src/dash/views/bidders/Bidders/SmartFloors/contexts';
import { ISmartFloorsLiveConfig } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveView/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const smartFloorsScalesInfo = (): { scales: Record<string, IScale>; numVisibleAxes: number } => {
  return {
    scales: {
      base: { hide: false, orientation: 'left' },
      small: { hide: false, orientation: 'right' },
      large: { hide: true, orientation: 'right' },
    },
    numVisibleAxes: 2,
  };
};

export const SmartFloorsLive = () => {
  const { SmartFloorsStore } = useSmartFloorsLiveStore();
  const { t } = usePublicaTranslation();

  const selectedSmartFloor = SmartFloorsStore.selectedSmartFloor;
  const selectedSiteName = SmartFloorsStore.selectedSiteName;
  const selectedSiteID = SmartFloorsStore.selectedSiteID;
  const selectedSmartFloorIDForRealtimeQuery = selectedSmartFloor.ID as number;

  const liveCfg = SmartFloorsStore.Live as ISmartFloorsLiveConfig;
  const {
    defaultState,
    chartMetricsByBarMetricKey,
    chartMetricsByLineMetricKey,
    dropdownForBarMetrics,
    dropdownForLineMetrics,
  } = liveCfg;
  const { session } = useBaseStore();

  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [activeChartMetrics, setActiveChartMetrics] = useState(defaultState.activeChartMetrics);

  // states for the two cards
  const [selectedBarMetricKey, setSelectedBarMetric] = useState(
    defaultState.activeCardBarMetricKey,
  ); // this sets BidRequests, CPM, Responses etc
  const [selectedLineMetricKey, setSelectedLineMetric] = useState(
    defaultState.activeCardLineMetricKey,
  ); // this sets OptimziedBidFloor, ControlBidFloor etc.

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [data, setData] = useState({} as IDashboardLiveData);
  const [isDataRefreshActive, setIsDataRefreshActive] = useState(false);
  const [lastRefreshedTime, setLastRefreshedTime] = useState('');
  const [bidFloorDomain, setBidFloorDomain] = useState<Array<AxisDomain>>([
    Math.max(0, selectedSmartFloor.MinBidFloor - 1),
    selectedSmartFloor.MaxBidFloor + 1,
  ]);

  useEffect(() => {
    getSmartFloorsLiveData();
  }, [SmartFloorsStore.selectedSiteID]);

  useInterval(
    () => {
      getSmartFloorsLiveData(true);
    },
    isDataRefreshActive ? 60 * 1000 : null,
  );

  const getSmartFloorsLiveData = (isRefreshCall?: boolean) => {
    if (!isRefreshCall) {
      setLoadState(LoadingStateLoading);
    }

    if (selectedSmartFloorIDForRealtimeQuery === 0) {
      unstable_batchedUpdates(() => {
        setData({
          LiveData: [] as IDashboardChartData[],
          CardMetrics: {} as IDashboardHomeCardMetrics,
        } as IDashboardLiveData);
        setBidFloorDomain([0, 30]);
        setLoadState(LoadingStateNoData);
      });
      return;
    }

    const startUnixTimestamp = moment().minute(0).add(-12, 'hours').unix(); // 12 hours ago
    const endUnixTimestamp = moment().add(1, 'minutes').unix();
    getSmartFloorLiveData(
      session.publisher.ID,
      selectedSmartFloorIDForRealtimeQuery,
      startUnixTimestamp,
      endUnixTimestamp,
      session.publisher.Timezone,
    )
      .then((smartFloorsLiveData) => {
        const isDataEmpty = isEmpty(smartFloorsLiveData);
        unstable_batchedUpdates(() => {
          setData(smartFloorsLiveData);
          setBidFloorDomain([
            Number(
              Math.min(
                ...smartFloorsLiveData?.LiveData?.map((datum) =>
                  Math.min(
                    datum.BidFloorControl,
                    datum.BidFloorHigh,
                    datum.BidFloorLow,
                    datum.BidFloorOptimized,
                  ),
                ),
              ).toFixed(2),
            ),
            Number(
              Math.max(
                ...smartFloorsLiveData?.LiveData?.map((datum) =>
                  Math.max(
                    datum.BidFloorControl,
                    datum.BidFloorHigh,
                    datum.BidFloorLow,
                    datum.BidFloorOptimized,
                  ),
                ),
              ).toFixed(2),
            ) + 1,
          ]);
          setLoadState(isDataEmpty ? LoadingStateNoData : LoadingStateHasData);
          setIsDataRefreshActive(true);
          setLastRefreshedTime(moment().format('HH:mm:ss'));
          if (isDataEmpty) {
            setSnackbarError(false);
            setSnackbarMessage('No Live Data Found.');
          }
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          if (!isDataRefreshActive) {
            setData({
              LiveData: [] as IDashboardChartData[],
              CardMetrics: {} as IDashboardHomeCardMetrics,
            } as IDashboardLiveData);
          }
          setBidFloorDomain([
            Math.max(0, selectedSmartFloor.MinBidFloor - 1),
            selectedSmartFloor.MaxBidFloor + 1,
          ]);
          setLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(
            !isDataRefreshActive
              ? t('SnackbarErrorRetrievingData')
              : t('SnackbarErrorRefreshingDataLastUpdatedAtTimestamp', {
                  timestamp: lastRefreshedTime,
                }),
          );
        });
      });
  };

  const handleOnChangeCardMetric = (
    metricKey: string,
    cardIndex: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    const updatedActiveChartMetrics: IDashboardChartMetric[] = [];

    const currSelectedLineMetric = dropdownForLineMetrics[selectedLineMetricKey].key;
    const currSelectedBarMetric = dropdownForBarMetrics[selectedBarMetricKey].key;

    let newLineMetricKey = currSelectedLineMetric as IDashboardHomeCardMetricKey;
    let newBarMetricKey = currSelectedBarMetric as IDashboardHomeCardMetricKey;

    if (isDisplayedOnChart) {
      switch (cardIndex) {
        case 0:
          updatedActiveChartMetrics.push(
            ...chartMetricsByBarMetricKey[metricKey],
            chartMetricsByLineMetricKey[currSelectedLineMetric],
          );
          newBarMetricKey = metricKey as IDashboardHomeCardMetricKey;
          break;
        case 1:
          updatedActiveChartMetrics.push(
            ...chartMetricsByBarMetricKey[currSelectedBarMetric],
            chartMetricsByLineMetricKey[metricKey],
          );
          newLineMetricKey = metricKey as IDashboardHomeCardMetricKey;
      }
    }

    unstable_batchedUpdates(() => {
      if (isDisplayedOnChart) {
        setActiveChartMetrics(updatedActiveChartMetrics);
        setSelectedBarMetric(newBarMetricKey);
        setSelectedLineMetric(newLineMetricKey);
      }
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  return (
    <Div>
      <Cols style={{ alignItems: 'center' }} marginBottom={spacing.small}>
        <Col displayFlex marginRight={spacing.extraSmall}>
          <IconV2 large name='loader' color={publicaColors.darkGray} />
        </Col>
        <Col marginRight={spacing.small}>
          <Text small heavy tKey='OptimizationInsights' />
        </Col>
        <Col>
          <Text
            extraSmall
            muted
            bold
            style={{
              background: publicaColors.veryLightGray,
              borderRadius: borderRadius.base,
              padding: `0 ${spacing.extraSmall}`,
            }}
            tKey='Last12H'
          />
        </Col>
      </Cols>
      {isEmpty(data?.LiveData) ? (
        <Text
          extraSmall
          style={{
            width: 'fit-content',
            background: publicaColors.lighterYellow,
            borderRadius: borderRadius.small,
            padding: `0 ${spacing.extraSmall}`,
          }}
          tKey='BiddersMessageSmartFloorsOptimizationInsights'
        />
      ) : (
        <Rows
          style={{
            background: publicaColors.white,
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: borderRadius.large,
          }}
        >
          <Row largeHorizontalPadding>
            <Cols wrap style={{ alignItems: 'center' }}>
              <Col>
                <Cols gap={spacing.base}>
                  <Col>
                    <Text extraSmall inlineBlock tKey='Showing' />
                  </Col>
                  <Col>
                    {selectedSiteName && selectedSiteID ? (
                      <Text inlineBlock bold>
                        {selectedSiteName} ({selectedSiteID})
                      </Text>
                    ) : (
                      <Text inlineBlock bold tKey='NoItemSelected' tProps={{ item: t('Site') }} />
                    )}
                  </Col>
                </Cols>
              </Col>

              <Col marginLeft={spacing.large}>
                <Cols style={{ alignItems: 'center' }}>
                  <Col style={{ marginLeft: spacing.base }}>
                    <Div
                      style={{
                        width: '1em',
                        height: '1em',
                        borderRadius: '50%',
                        background: CHART_COLORS[3][0],
                      }}
                    />
                  </Col>
                  <Col width={192} style={{ marginLeft: spacing.extraSmall }}>
                    <DashboardCard
                      hideCardColorBar
                      showCardValue={false}
                      loadState={loadState}
                      cardMetrics={data.CardMetrics}
                      availableMetrics={dropdownForLineMetrics}
                      selectedMetric={dropdownForLineMetrics[selectedLineMetricKey]}
                      index={1}
                      isCompareEnabled={false}
                      isDisplayedOnChart={
                        !!activeChartMetrics.find(
                          (activeMetric) => activeMetric.key === selectedLineMetricKey,
                        )
                      }
                      handleOnChangeCardMetric={handleOnChangeCardMetric}
                      style={{ padding: '0 8px' }}
                    />
                  </Col>

                  <Col displayFlex style={{ marginLeft: spacing.base }}>
                    <Div
                      style={{
                        width: '0.25em',
                        height: '1em',
                        background: CHART_COLORS[0][0],
                        display: 'inline-block',
                        borderRadius: '0.25em 0 0 0.25em',
                      }}
                    />
                    <Div
                      style={{
                        width: '0.25em',
                        height: '1em',
                        background: CHART_COLORS[4][0],
                        display: 'inline-block',
                      }}
                    />
                    <Div
                      style={{
                        width: '0.25em',
                        height: '1em',
                        background: CHART_COLORS[5][0],
                        display: 'inline-block',
                      }}
                    />
                    <Div
                      style={{
                        width: '0.25em',
                        height: '1em',
                        background: CHART_COLORS[2][0],

                        display: 'inline-block',
                        borderRadius: '0 0.25em 0.25em 0',
                      }}
                    />
                  </Col>
                  <Col style={{ marginLeft: spacing.extraSmall }}>
                    <Text tKey='YAxisMetric' />
                  </Col>
                  <Col width={192} style={{ marginLeft: spacing.extraSmall }}>
                    <DashboardCard
                      hideCardColorBar
                      showCardValue={false}
                      loadState={loadState}
                      cardMetrics={data.CardMetrics}
                      availableMetrics={dropdownForBarMetrics}
                      selectedMetric={dropdownForBarMetrics[selectedBarMetricKey]}
                      index={0}
                      isCompareEnabled={false}
                      isDisplayedOnChart={
                        !!activeChartMetrics.find((activeMetric) =>
                          activeMetric.key.includes(selectedBarMetricKey),
                        )
                      }
                      handleOnChangeCardMetric={handleOnChangeCardMetric}
                      style={{ padding: '0 8px' }}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>

          <Row largeHorizontalPadding>
            <CompareChart
              loadState={loadState}
              data={mergeCompareData(data.LiveData)}
              metrics={activeChartMetrics}
              metricsConfig={liveCfg.chartMetricsCfg}
              grouping={0}
              customGetScalesInfo={smartFloorsScalesInfo}
              customYAxisDomains={{
                small: bidFloorDomain,
              }}
              useGroupedToolTip={true}
            />
          </Row>
        </Rows>
      )}

      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
    </Div>
  );
};
